// src/components/NewsEditor.js
import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { getNewsItems, addNewsItem, updateNewsItem, deleteNewsItem } from '../utils/firestoreUtils';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../utils/firebase';
import Compressor from 'compressorjs';

const BLOCK_TYPES = [
  { value: 'heading',    label: 'Heading' },
  { value: 'subheading', label: 'Subheading' },
  { value: 'text',       label: 'Body Text' },
  { value: 'image',      label: 'Image' },
];

const TAGS = ['Announcement', 'Race Report', 'Technical', 'Community', 'Season Update', 'Sponsor'];

function emptyBlock(type = 'text') {
  return { type, content: '', url: '', caption: '', id: Date.now() + Math.random() };
}

// ── Block editor row ────────────────────────────────────────
function BlockRow({ block, onChange, onRemove, onMoveUp, onMoveDown, isFirst, isLast, uploading, onImageUpload }) {
  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-md)',
      padding: '12px 14px',
      marginBottom: 8,
    }}>
      {/* Block header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <DragIndicatorIcon sx={{ color: 'var(--text-tertiary)', fontSize: 18 }} />
        <TextField
          select size="small" value={block.type}
          onChange={e => onChange({ ...block, type: e.target.value, content: '', url: '', caption: '' })}
          sx={{ width: 140 }}
        >
          {BLOCK_TYPES.map(bt => <MenuItem key={bt.value} value={bt.value}>{bt.label}</MenuItem>)}
        </TextField>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          <button onClick={onMoveUp} disabled={isFirst}
            style={{ background: 'none', border: 'none', cursor: isFirst ? 'default' : 'pointer', color: isFirst ? 'var(--border-strong)' : 'var(--text-tertiary)', fontSize: '0.9rem', padding: '2px 4px' }}>↑</button>
          <button onClick={onMoveDown} disabled={isLast}
            style={{ background: 'none', border: 'none', cursor: isLast ? 'default' : 'pointer', color: isLast ? 'var(--border-strong)' : 'var(--text-tertiary)', fontSize: '0.9rem', padding: '2px 4px' }}>↓</button>
          <button onClick={onRemove}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--red)', display: 'flex', alignItems: 'center', padding: '2px 4px' }}>
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* Block content */}
      {block.type === 'image' ? (
        <div>
          {block.url ? (
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <img src={block.url} alt="" style={{ maxWidth: '100%', maxHeight: 160, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
              <button onClick={() => onChange({ ...block, url: '' })}
                style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,0.6)', border: 'none', borderRadius: '50%', color: '#fff', width: 22, height: 22, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11 }}>
                ✕
              </button>
            </div>
          ) : (
            <label className="file-upload-label" htmlFor={`img-${block.id}`} style={{ display: 'inline-flex' }}>
              {uploading ? 'Uploading…' : '＋ Upload Image'}
            </label>
          )}
          <input id={`img-${block.id}`} type="file" accept="image/*" style={{ display: 'none' }}
            onChange={e => onImageUpload(e, block)} />
          <TextField
            size="small" fullWidth placeholder="Caption (optional)"
            value={block.caption || ''} onChange={e => onChange({ ...block, caption: e.target.value })}
            sx={{ mt: 1 }} />
        </div>
      ) : (
        <TextField
          size="small" fullWidth multiline
          rows={block.type === 'text' ? 4 : 1}
          placeholder={block.type === 'heading' ? 'Heading text…' : block.type === 'subheading' ? 'Subheading text…' : 'Body text…'}
          value={block.content || ''}
          onChange={e => onChange({ ...block, content: e.target.value })}
        />
      )}
    </div>
  );
}

// ── Post editor dialog ──────────────────────────────────────
function PostEditorDialog({ open, onClose, editItem, onSaved }) {
  const [title,    setTitle]    = useState('');
  const [date,     setDate]     = useState('');
  const [tag,      setTag]      = useState('');
  const [blocks,   setBlocks]   = useState([emptyBlock('text')]);
  const [saving,   setSaving]   = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errors,   setErrors]   = useState({});

  useEffect(() => {
    if (editItem) {
      setTitle(editItem.title || '');
      setDate(editItem.date || '');
      setTag(editItem.tag || '');
      setBlocks(editItem.blocks?.length ? editItem.blocks.map(b => ({ ...b, id: b.id || Date.now() + Math.random() })) : [emptyBlock('text')]);
    } else {
      setTitle(''); setDate(''); setTag('');
      setBlocks([emptyBlock('text')]);
    }
    setErrors({});
  }, [editItem, open]);

  const updateBlock = (idx, updated) => setBlocks(bs => bs.map((b, i) => i === idx ? updated : b));
  const removeBlock = (idx) => setBlocks(bs => bs.filter((_, i) => i !== idx));
  const addBlock = (type) => setBlocks(bs => [...bs, emptyBlock(type)]);
  const moveBlock = (idx, dir) => {
    const nb = [...blocks];
    const swap = idx + dir;
    if (swap < 0 || swap >= nb.length) return;
    [nb[idx], nb[swap]] = [nb[swap], nb[idx]];
    setBlocks(nb);
  };

  const handleImageUpload = (e, block) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    new Compressor(file, {
      quality: 0.8, maxWidth: 1600, maxHeight: 1200,
      success: async (compressed) => {
        const r = ref(storage, `news/${Date.now()}_${file.name}`);
        await uploadBytes(r, compressed);
        const url = await getDownloadURL(r);
        setBlocks(bs => bs.map(b => b.id === block.id ? { ...b, url } : b));
        setUploading(false);
      },
      error: () => setUploading(false),
    });
  };

  const handleSave = async () => {
    const e = {};
    if (!title.trim()) e.title = true;
    if (!date) e.date = true;
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    // Strip internal id field before saving
    const cleanBlocks = blocks.map(({ id, ...rest }) => rest);
    const payload = { title, date, tag, blocks: cleanBlocks };
    if (editItem) { await updateNewsItem(editItem.id, payload); }
    else          { await addNewsItem(payload); }
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-xl)', maxHeight: '90vh' } }}>
      <DialogTitle sx={{ p: '20px 24px 16px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', letterSpacing: '0.04em', lineHeight: 1 }}>
              {editItem ? 'Edit Post' : 'New News Post'}
            </div>
            <div style={{ fontSize: '0.74rem', color: 'var(--text-tertiary)', marginTop: 3 }}>Blocks are stacked top to bottom</div>
          </div>
          <IconButton onClick={onClose} size="small"><CloseIcon fontSize="small" /></IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ p: '20px 24px', overflowY: 'auto' }}>
        {/* Meta */}
        <div className="field-section"><div className="field-section-line" /><span className="field-section-label">Post Info</span><div className="field-section-line" /></div>
        <TextField label="Title" value={title} onChange={e => { setTitle(e.target.value); setErrors(p => ({...p, title: false})); }}
          error={errors.title} helperText={errors.title ? 'Required' : ''} size="small" fullWidth sx={{ mb: 1.5 }} />
        <div className="field-row" style={{ marginBottom: 14 }}>
          <TextField type="date" label="Date" value={date}
            onChange={e => { setDate(e.target.value); setErrors(p => ({...p, date: false})); }}
            error={errors.date} helperText={errors.date ? 'Required' : ''} size="small" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField select label="Tag (optional)" value={tag} onChange={e => setTag(e.target.value)} size="small" fullWidth>
            <MenuItem value="">None</MenuItem>
            {TAGS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
        </div>

        {/* Blocks */}
        <div className="field-section"><div className="field-section-line" /><span className="field-section-label">Content Blocks</span><div className="field-section-line" /></div>
        {blocks.map((block, idx) => (
          <BlockRow
            key={block.id}
            block={block}
            onChange={updated => updateBlock(idx, updated)}
            onRemove={() => removeBlock(idx)}
            onMoveUp={() => moveBlock(idx, -1)}
            onMoveDown={() => moveBlock(idx, 1)}
            isFirst={idx === 0}
            isLast={idx === blocks.length - 1}
            uploading={uploading}
            onImageUpload={handleImageUpload}
          />
        ))}

        {/* Add block buttons */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 4 }}>
          {BLOCK_TYPES.map(bt => (
            <button key={bt.value} onClick={() => addBlock(bt.value)}
              style={{ padding: '6px 14px', borderRadius: 'var(--radius-pill)', border: '1px dashed var(--border-strong)', background: 'transparent', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-secondary)', transition: 'all var(--transition)' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border-strong)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}>
              ＋ {bt.label}
            </button>
          ))}
        </div>

        <div className="modal-actions">
          <div />
          <Button onClick={handleSave} disabled={saving} variant="contained" sx={{ textTransform: 'none', fontWeight: 700, px: 3 }}>
            {saving ? 'Saving…' : editItem ? 'Save Changes' : 'Publish Post'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Main NewsEditor (embedded in AdminPage) ─────────────────
function NewsEditor() {
  const [items,       setItems]       = useState([]);
  const [openEditor,  setOpenEditor]  = useState(false);
  const [editItem,    setEditItem]    = useState(null);
  const [confirmDel,  setConfirmDel]  = useState(null);

  const load = () => getNewsItems().then(setItems);
  useEffect(() => { load(); }, []);

  const fmtDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <div className="admin-section">
        <div className="admin-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="admin-section-title">News Posts</span>
          <button
            onClick={() => { setEditItem(null); setOpenEditor(true); }}
            style={{ background: 'var(--accent)', border: 'none', borderRadius: 'var(--radius-md)', color: '#fff', padding: '6px 14px', fontFamily: 'var(--font-body)', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}>
            + New Post
          </button>
        </div>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Date</th>
                <th>Tag</th>
                <th>Blocks</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-tertiary)', padding: '32px 0' }}>No news posts yet</td></tr>
              )}
              {items.map(item => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 600 }}>{item.title}</td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', whiteSpace: 'nowrap' }}>{fmtDate(item.date)}</td>
                  <td>
                    {item.tag && (
                      <span style={{ background: 'var(--accent-subtle)', color: 'var(--accent)', fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 'var(--radius-pill)' }}>
                        {item.tag}
                      </span>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-tertiary)', fontSize: '0.82rem' }}>{item.blocks?.length || 0} blocks</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="admin-btn-warn" onClick={() => { setEditItem(item); setOpenEditor(true); }}>Edit</button>
                    <button className="admin-btn-deny" style={{ marginLeft: 4 }} onClick={() => setConfirmDel(item)}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PostEditorDialog
        open={openEditor}
        onClose={() => { setOpenEditor(false); setEditItem(null); }}
        editItem={editItem}
        onSaved={load}
      />

      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)}
        PaperProps={{ sx: { borderRadius: 'var(--radius-xl)' } }}>
        <DialogTitle>Delete Post</DialogTitle>
        <DialogContent>Permanently delete "{confirmDel?.title}"?</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDel(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={async () => { await deleteNewsItem(confirmDel.id); setConfirmDel(null); load(); }}
            color="error" variant="contained" sx={{ textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default NewsEditor;
